/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  FormControl,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../../api";
import convertBase64 from "../../../utils/convertBase64";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { Query_Keys } from "../../../lib/queryKeys";
import { useDebounce } from "../../../utils/useDebounce";
import { PerPage } from "../../../constants/perPage";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { LoadingSpinner } from "../../../components/loading_spinner";
import { useOtMutation } from "../../../hooks/mutations/useOtMutation";
import { restaurantGroupMutationKeyGroups } from "../../../hooks/mutations/queryKeyGroups/restaurantGroupMutationKeys";
import {
  SubGroupDialogHeader,
  SubGroupDialog,
  SubGroupDialogTitle,
  SubGroupDialogContent,
  FormContent,
  FieldStack,
  LogoSection,
  LogoSectionHeader,
  LogoSectionSubheader,
  SharedRequirementsCard,
  RequirementList,
  RequirementItem,
  LogoGrid,
  LogoCard,
  LogoPreviewFrame,
  LogoPreviewImage,
  LogoActions,
  UploadButton,
  StyledSelect,
  TopFieldsGrid,
  HelperError,
  CustomStyledFormFieldText,
  StyledTextField,
  StyledAddButton,
  StyledCancelButton,
  SubtitleText,
  CLOSE_ICON_COLOR,
  REQUIRED_FIELD_ASTERISK_COLOR,
  StyledImageRequirementsTitle,
  StyledDialogActions,
} from "./styles";
import { Loader } from "../../../components/loader";

const LOGO_ACCEPTED_TYPES = ["image/png", "image/jpeg"];
const LOGO_MAX_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB
const LOGO_FIELDS = [
  {
    key: "display_profile_photo",
    label: "Display Profile Photo",
    description: "Use this for Discount Card Logo. Required size: 512 x 512 pixels.",
    previewBackground: "#f8fafc",
    requiredWidth: 512,
    requiredHeight: 512,
  },
  {
    key: "wallet_dark_logo",
    label: "Wallet Dark Logo",
    description: "Use this for dark wallet cards. Required size: 1125 x 369 pixels.",
    previewBackground: "#000000",
    requiredWidth: 1125,
    requiredHeight: 369,
  },
  {
    key: "wallet_light_logo",
    label: "Wallet Light Logo",
    description: "Use this for light wallet cards. Required size: 1125 x 369 pixels.",
    previewBackground: "#ffffff",
    requiredWidth: 1125,
    requiredHeight: 369,
  },
];
const CREATE_MODE = "create";
const EDIT_MODE = "edit";
const EMPTY_INITIAL_VALUES = {};
const EMPTY_RESTAURANT_GROUPS = [];
// NOTE: This placeholder is consistent with the placeholder image used throughout the
// application. Replacing it with a bundled SVG asset can be done in a separate ticket.
const PLACEHOLDER_PHOTO = "https://placehold.co/";
const RESTAURANT_GROUP_SEARCH_RESET_DELAY_MS = 1000;

const getFirstError = (value) => {
  if (Array.isArray(value)) {
    return value[0];
  }

  return value || "";
};

const validatePhotoFile = async (file, { fieldLabel, requiredWidth, requiredHeight } = {}) => {
  if (!file) {
    return { valid: false, error: `Please upload a ${fieldLabel || "logo"}.` };
  }

  if (!LOGO_ACCEPTED_TYPES.includes(file.type)) {
    return { valid: false, error: "Please upload a PNG or JPEG file." };
  }

  if (file.size > LOGO_MAX_SIZE_BYTES) {
    return { valid: false, error: "File size must be 1 MB or smaller." };
  }

  const isValidDimensions = await new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(
        image.width === requiredWidth &&
          image.height === requiredHeight,
      );
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Failed to load image"));
    };

    image.src = objectUrl;
  }).catch(() => false);

  if (!isValidDimensions) {
    return {
      valid: false,
      error: `The dimensions must be ${requiredWidth} x ${requiredHeight} pixels.`,
    };
  }

  return { valid: true, error: "" };
};

const createLogoState = (url = "") => ({
  url,
  data: "",
  error: "",
});

const buildInitialModalState = (initialValues = {}) => ({
  name: initialValues.name || "",
  description: initialValues.description || "",
  restaurantGroupId: initialValues.restaurantGroupId ? String(initialValues.restaurantGroupId) : "",
  restaurantGroupLabel: initialValues.restaurantGroupLabel || "",
  displayProfilePhoto: createLogoState(initialValues.displayProfilePhotoUrl || ""),
  walletDarkLogo: createLogoState(initialValues.walletDarkLogoUrl || ""),
  walletLightLogo: createLogoState(initialValues.walletLightLogoUrl || ""),
});

const getRestaurantSubGroupMutationMessage = (mode, action = "success") => {
  const isEditMode = mode === EDIT_MODE;

  if (action === "error") {
    return isEditMode
      ? "Failed to update restaurant sub group"
      : "Failed to create restaurant sub group";
  }

  return isEditMode
    ? "Restaurant sub group has been updated successfully"
    : "Restaurant sub group has been created successfully";
};

const RestaurantSubGroupModal = ({
  open,
  onClose,
  onExited,
  mode = CREATE_MODE,
  subGroupId,
  isLoading = false,
  initialValues = EMPTY_INITIAL_VALUES,
}) => {
  const isEditMode = mode === EDIT_MODE;
  const showNotification = useNotificationContext();
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [restaurantGroupId, setRestaurantGroupId] = React.useState("");
  const [restaurantGroupLabel, setRestaurantGroupLabel] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [submitDisabled, setSubmitDisabled] = React.useState(false);
  const [displayProfilePhoto, setDisplayProfilePhoto] = React.useState({
    url: "",
    data: "",
    error: "",
  });
  const [walletDarkLogo, setWalletDarkLogo] = React.useState({
    url: "",
    data: "",
    error: "",
  });
  const [walletLightLogo, setWalletLightLogo] = React.useState({
    url: "",
    data: "",
    error: "",
  });
  const [photoTouched, setPhotoTouched] = React.useState(false);
  const [restaurantGroupSearch, setRestaurantGroupSearch] = React.useState("");
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] =
    React.useState([]);
  const restaurantGroupSearchResetTimeoutRef = React.useRef(null);
  const debouncedRestaurantGroupSearch = useDebounce(
    restaurantGroupSearch.trim(),
    300,
  );

  const resetForm = React.useCallback((values = {}) => {
    const nextState = buildInitialModalState(values);

    setName(nextState.name);
    setDescription(nextState.description);
    setRestaurantGroupId(nextState.restaurantGroupId);
    setRestaurantGroupLabel(nextState.restaurantGroupLabel);
    setErrors({});
    setSubmitDisabled(false);
    setDisplayProfilePhoto(nextState.displayProfilePhoto);
    setWalletDarkLogo(nextState.walletDarkLogo);
    setWalletLightLogo(nextState.walletLightLogo);
    setPhotoTouched(false);
    setRestaurantGroupSearch("");
  }, []);

  React.useEffect(() => {
    if (open) {
      resetForm(initialValues);
    }
  }, [open, resetForm, mode, initialValues]);

  React.useEffect(
    () => () => {
      if (restaurantGroupSearchResetTimeoutRef.current) {
        window.clearTimeout(restaurantGroupSearchResetTimeoutRef.current);
      }
    },
    [],
  );

  const fetchRestaurantGroupList = React.useCallback(
    async ({ pageParam = 1 }) => {
      const response = await api.get(
        "/v1/admin/dropdowns/restaurant_groups_list",
        {
          params: {
            page: pageParam,
            per_page: PerPage,
            search: debouncedRestaurantGroupSearch || undefined,
          },
        },
      );

      return response?.data?.map(normalizeRestaurantGroup) || [];
    },
    [debouncedRestaurantGroupSearch],
  );

  const {
    data: restaurantGroups = EMPTY_RESTAURANT_GROUPS,
    isFetching: restaurantGroupsFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
    }),
    queryFn: fetchRestaurantGroupList,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: open,
  });

  const {
    rootRef: restaurantGroupMenuRef,
    targetRef: restaurantGroupLoaderRef,
  } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
  });

  React.useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) =>
      mergeOptionsIfChanged(currentOptions, restaurantGroups, "label", ["value"]),
    );
  }, [restaurantGroups]);

  const clearFieldError = React.useCallback((field) => {
    setErrors((previousErrors) => {
      if (!previousErrors?.[field]) {
        return previousErrors;
      }

      const nextErrors = { ...previousErrors };
      delete nextErrors[field];
      return nextErrors;
    });
  }, []);

  const handleMenuClose = React.useCallback(() => {
    if (restaurantGroupSearchResetTimeoutRef.current) {
      window.clearTimeout(restaurantGroupSearchResetTimeoutRef.current);
    }

    restaurantGroupSearchResetTimeoutRef.current = window.setTimeout(() => {
      setRestaurantGroupSearch("");
      restaurantGroupSearchResetTimeoutRef.current = null;
    }, RESTAURANT_GROUP_SEARCH_RESET_DELAY_MS);
  }, []);

  // NOTE: normalizeRestaurantGroup maps
  // { label: restaurant_group_id, value: restaurant_group_name }.
  //
  // This is intentionally inverted from the conventional
  // { label: displayText, value: id } pattern so the Select value
  // stores the restaurant_group_id while value is used for display.
  // Matches normalizeRestaurantGroup in dropdownInfiniteQuery.
  const getRestaurantGroupDisplayNameById = React.useCallback(
    (id) => {
      const restaurantGroup = restaurantGroupOptionsCache.find(
        (option) => String(option?.label) === String(id),
      );
      return restaurantGroup?.value || "";
    },
    [restaurantGroupOptionsCache],
  );

  const handleLogoUpload = async (event, fieldKey) => {
    const file = event.target.files?.[0];
    const fieldConfig = LOGO_FIELDS.find((field) => field.key === fieldKey);

    const setLogoState = {
      display_profile_photo: setDisplayProfilePhoto,
      wallet_dark_logo: setWalletDarkLogo,
      wallet_light_logo: setWalletLightLogo,
    }[fieldKey];

    if (!file) {
      return;
    }

    if (fieldKey === "display_profile_photo") {
      setPhotoTouched(true);
    }

    const validationResult = await validatePhotoFile(file, fieldConfig);

    if (!validationResult.valid) {
      setLogoState((previous) => ({
        ...previous,
        error: validationResult.error,
      }));
      setErrors((previous) => ({
        ...previous,
        [fieldKey]: validationResult.error,
      }));
      event.target.value = "";
      return;
    }

    const base64Image = await convertBase64(file);

    setLogoState({
      url: base64Image,
      data: base64Image,
      error: "",
    });
    clearFieldError(fieldKey);
    event.target.value = "";
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!name.trim()) {
      nextErrors.name = "Name can't be blank";
    }

    if (!restaurantGroupId) {
      nextErrors.restaurant_group_id = "Please select a restaurant group.";
    }

    if (displayProfilePhoto.error) {
      nextErrors.display_profile_photo = displayProfilePhoto.error;
    }

    if (walletDarkLogo.error) {
      nextErrors.wallet_dark_logo = walletDarkLogo.error;
    }

    if (walletLightLogo.error) {
      nextErrors.wallet_light_logo = walletLightLogo.error;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const { mutateAsync: mutateRestaurantSubGroup } = useOtMutation({
    mutationFn: async (payload) => {
      if (isEditMode) {
        return api.put(`/v1/admin/restaurant_sub_groups/${subGroupId}`, payload);
      }

      return api.post("/v1/admin/restaurant_sub_groups", payload);
    },
    invalidateKeys: isEditMode
      ? [...restaurantGroupMutationKeyGroups.subGroupUpdate]
      : [...restaurantGroupMutationKeyGroups.subGroupCreate],
    successMessage: () => getRestaurantSubGroupMutationMessage(mode),
    errorMessage: ({ error }) => {
      const apiErrors = error?.response?.data?.errors || {};

      if (Object.keys(apiErrors).length > 0) {
        return null;
      }

      return getRestaurantSubGroupMutationMessage(mode, "error");
    },
    onSuccess: () => {
      onClose();
    },
    onError: (error) => {
      setErrors((previousErrors) => ({
        ...previousErrors,
        ...(error?.response?.data?.errors || {}),
      }));
      setSubmitDisabled(false);
    },
  });

  const handleSubmit = async () => {
    setErrors({});

    if (!validateForm()) {
      showNotification(
        `Please correct the highlighted fields before ${isEditMode ? "updating" : "creating"} the restaurant sub group.`,
        "error",
      );
      return;
    }

    setSubmitDisabled(true);

    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      restaurant_group_id: Number.parseInt(restaurantGroupId, 10),
      ...(displayProfilePhoto.data
        ? { display_profile_photo: displayProfilePhoto.data }
        : {}),
      ...(walletDarkLogo.data ? { wallet_dark_logo: walletDarkLogo.data } : {}),
      ...(walletLightLogo.data ? { wallet_light_logo: walletLightLogo.data } : {}),
    };

    try {
      await mutateRestaurantSubGroup(payload);
    } catch {
      // Handled in the mutation callbacks.
    }
  };

  const handleClose = () => {
    onClose();
  };

  const isFormDisabled = Boolean(isEditMode && isLoading);

  return (
    <SubGroupDialog
      open={open}
      onClose={handleClose}
      TransitionProps={{ onExited }}
      fullWidth
      maxWidth={false}
      scroll="paper"
      data-testid={isEditMode ? "restaurant-sub-group-edit-modal" : "restaurant-sub-group-create-modal"}
      aria-labelledby={isEditMode ? "restaurant-sub-group-edit-modal-title" : "restaurant-sub-group-create-modal-title"}
    >
      <SubGroupDialogHeader>
        <div>
          <SubGroupDialogTitle
            id={isEditMode ? "restaurant-sub-group-edit-modal-title" : "restaurant-sub-group-create-modal-title"}
            tabIndex={-1}
          >
            {isEditMode ? "Edit Restaurant Sub Group" : "Create Restaurant Sub Group"}
            {isLoading && " - Loading details..."}
          </SubGroupDialogTitle>
          <SubtitleText>
            {isEditMode
              ? "Update the sub group details and keep the restaurant group fixed."
              : "Name your sub group, associate it with its restaurant group, and upload its display logo."}
          </SubtitleText>
        </div>
        <IconButton
          onClick={handleClose}
          aria-label={`Close ${isEditMode ? "edit" : "create"} restaurant sub group modal`}
        >
          <CloseIcon sx={{ color: CLOSE_ICON_COLOR }} />
        </IconButton>
      </SubGroupDialogHeader>

      <SubGroupDialogContent dividers={false}>
        {isLoading ? (
          <Box
            sx={{
              minHeight: "520px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            data-testid="restaurant-sub-group-loading"
          >
            <Loader />
          </Box>
        ) : (
        <FormContent>
          {errors?.base ? (
            <Alert severity="error">{getFirstError(errors.base)}</Alert>
          ) : null}

          <TopFieldsGrid>
            <FieldStack>
              <CustomStyledFormFieldText>
                Restaurant Sub Group Name<span style={{ color: REQUIRED_FIELD_ASTERISK_COLOR }}>*</span>
              </CustomStyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                value={name}
                disabled={isFormDisabled}
                onChange={(event) => {
                  setName(event.target.value);
                  clearFieldError("name");
                }}
                error={Boolean(errors?.name)}
                helperText={getFirstError(errors?.name)}
                inputProps={{
                  "data-testid": "restaurant-sub-group-name-input",
                  maxLength: 255, // As per backend validation rules
                }}
              />
            </FieldStack>

            <FieldStack>
              <CustomStyledFormFieldText>
                Restaurant Group<span style={{ color: REQUIRED_FIELD_ASTERISK_COLOR }}>*</span>
              </CustomStyledFormFieldText>
              {isEditMode ? (
                <StyledTextField
                  fullWidth
                  size="small"
                  value={restaurantGroupLabel}
                  disabled
                  inputProps={{
                    "data-testid": "restaurant-sub-group-parent-display",
                  }}
                />
              ) : (
                <FormControl
                  fullWidth
                  error={Boolean(errors?.restaurant_group_id)}
                >
                  <StyledSelect
                    value={restaurantGroupId}
                    disabled={isFormDisabled}
                    displayEmpty
                    onChange={(event) => {
                      setRestaurantGroupId(event.target.value);
                      clearFieldError("restaurant_group_id");
                    }}
                    onClose={handleMenuClose}
                    MenuProps={{
                      PaperProps: {
                        ref: restaurantGroupMenuRef,
                        sx: { maxHeight: 400 },
                      },
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    renderValue={() =>
                      getRestaurantGroupDisplayNameById(restaurantGroupId) ||
                      "Select restaurant group"
                    }
                    data-testid="restaurant-sub-group-parent-select"
                  >
                    <DropdownSearchBar
                      changeSearchText={setRestaurantGroupSearch}
                      placeholder="Search restaurant groups"
                      isFetching={restaurantGroupsFetching}
                    />
                    {restaurantGroups?.map((restaurantGroup) => (
                      <StyledMenuItem
                        key={restaurantGroup?.label}
                        value={restaurantGroup?.label}
                      >
                        {String(restaurantGroupId) ===
                        String(restaurantGroup?.label) ? (
                          <SelectedMenuItem data={restaurantGroup?.value} />
                        ) : (
                          restaurantGroup?.value
                        )}
                      </StyledMenuItem>
                    ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantGroupsPage}
                      targetRef={restaurantGroupLoaderRef}
                    />
                  </StyledSelect>
                  <HelperError>
                    {getFirstError(errors?.restaurant_group_id)}
                  </HelperError>
                </FormControl>
              )}
            </FieldStack>
          </TopFieldsGrid>

          <FieldStack>
            <CustomStyledFormFieldText>Description</CustomStyledFormFieldText>
            <StyledTextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              value={description}
              disabled={isFormDisabled}
              onChange={(event) => {
                setDescription(event.target.value);
                clearFieldError("description");
              }}
              error={Boolean(errors?.description)}
              helperText={getFirstError(errors?.description)}
              inputProps={{
                "data-testid": "restaurant-sub-group-description-input",
                maxLength: 1000, // As per backend validation rules
              }}
            />
          </FieldStack>

          <FieldStack>
            <LogoSection>
              <LogoSectionHeader>Logo assets</LogoSectionHeader>
              <LogoSectionSubheader>
                Upload logo assets for the display profile photo, wallet dark logo, and wallet light logo variants.
              </LogoSectionSubheader>

              <SharedRequirementsCard>
                <StyledImageRequirementsTitle>
                  Shared requirements
                </StyledImageRequirementsTitle>
                <RequirementList>
                  <RequirementItem>Supported formats: PNG and JPEG</RequirementItem>
                  <RequirementItem>Maximum file size: 1 MB</RequirementItem>
                </RequirementList>
              </SharedRequirementsCard>

              <LogoGrid>
                {LOGO_FIELDS.map((field) => {
                  const logoState = {
                    display_profile_photo: displayProfilePhoto,
                    wallet_dark_logo: walletDarkLogo,
                    wallet_light_logo: walletLightLogo,
                  }[field.key];
                  const hasCustomLogo = Boolean(logoState.data || logoState.url);
                  const placeholderPhotoUrl = `${PLACEHOLDER_PHOTO}${field.requiredWidth}x${field.requiredHeight}/`;
                  const fieldError = errors?.[field.key];
                  const shouldShowError =
                    field.key === "display_profile_photo"
                      ? photoTouched && fieldError
                      : fieldError;

                  return (
                    <LogoCard key={field.key}>
                      <CustomStyledFormFieldText>
                        {field.label}
                      </CustomStyledFormFieldText>
                      <SubtitleText sx={{ mt: 0, mb: 1 }}>
                        {field.description}
                      </SubtitleText>

                      <LogoPreviewFrame sx={{ backgroundColor: field.previewBackground }}>
                        <LogoPreviewImage
                          src={logoState.data || logoState.url || placeholderPhotoUrl}
                          alt={hasCustomLogo ? `${field.label} preview` : `${field.label} placeholder`}
                          data-testid={`${field.key}-preview-image`}
                        />
                      </LogoPreviewFrame>

                      <LogoActions>
                        <UploadButton variant="outlined" component="label" disabled={isFormDisabled}>
                          {hasCustomLogo ? "Replace" : "Upload"}
                          <input
                            hidden
                            accept=".png,.jpg,.jpeg"
                            type="file"
                            data-testid={`${field.key}-upload-input`}
                            onChange={(event) => handleLogoUpload(event, field.key)}
                          />
                        </UploadButton>
                      </LogoActions>

                      {shouldShowError ? (
                        <HelperError>
                          {getFirstError(fieldError)}
                        </HelperError>
                      ) : null}
                    </LogoCard>
                  );
                })}
              </LogoGrid>
            </LogoSection>
          </FieldStack>
        </FormContent>
        )}
      </SubGroupDialogContent>

      <StyledDialogActions>
        <StyledCancelButton
          onClick={handleClose}
          variant="outlined"
          sx={{ textTransform: "none", borderRadius: "8px" }}
          data-testid="restaurant-sub-group-cancel-button"
        >
          Cancel
        </StyledCancelButton>
        <StyledAddButton
          onClick={handleSubmit}
          variant="contained"
          disabled={submitDisabled || isFormDisabled}
          data-testid="restaurant-sub-group-submit-button"
          sx={{ padding: "6px 16px !important" }}
        >
          {submitDisabled ? <LoadingSpinner isLoading={true} /> : isEditMode ? "Save" : "Create"}
        </StyledAddButton>
      </StyledDialogActions>
    </SubGroupDialog>
  );
};

export const CreateRestaurantSubGroupModal = (props) => (
  <RestaurantSubGroupModal {...props} mode={CREATE_MODE} />
);

export const EditRestaurantSubGroupModal = (props) => (
  <RestaurantSubGroupModal {...props} mode={EDIT_MODE} />
);

export default CreateRestaurantSubGroupModal;
